import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Select from 'react-select';
import { httpsCallable } from 'firebase/functions';
import { functionsInstance, db, analytics } from '../../firebase/firebase';
import { collection, onSnapshot, query, orderBy, doc, deleteDoc, getDocs } from 'firebase/firestore';
import { Chart } from 'chart.js/auto';
import { logEvent } from 'firebase/analytics';
import {
  Loader2, RefreshCw, DownloadCloud, Edit, Sparkles, Lightbulb, ArrowUp, ArrowDown,
  Clipboard, ClipboardCheck, ThumbsUp, AlertTriangle, Star, Save, Layers, BarChart3,
  Trash2, X, Video, Play, MousePointerClick, Flashlight
} from 'lucide-react';
import ConfirmationModal from '../ui/ConfirmationModal';

// Shared tier logic — mirrors the Keyword Research Tool's badge calculation, so both
// tools describe the same keyword the same way. Only real historical popularity (the
// TPT-scale number these thresholds were calibrated against) counts as a demand signal;
// our own internal searchCount is too sparse a metric to grade against these same
// thresholds, so it's never used here.
function computeOpportunityBadge({ competition, demandCount, hasDemandData }) {
  const base = 'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide shadow-sm border';

  if (competition == null) return null; // no real data (live or cached) — no badge

  if (!hasDemandData) {
    if (competition < 2000) return { icon: '🟢', text: 'Low Competition', className: `${base} bg-blue-50 text-blue-700 border-blue-200` };
    if (competition < 10000) return { icon: '🟡', text: 'Moderate Competition', className: `${base} bg-slate-50 text-slate-600 border-slate-200` };
    return { icon: '🔴', text: 'High Competition', className: `${base} bg-slate-50 text-slate-500 border-slate-200` };
  }
  if (demandCount > 20 && competition < 2000) {
    return { icon: '💰', text: 'Gold Mine', className: `${base} bg-gradient-to-r from-amber-400 to-yellow-500 text-white border-amber-300` };
  }
  if (demandCount > 50 && competition < 10000) {
    return { icon: '⭐', text: 'Excellent', className: `${base} bg-gradient-to-r from-sky-400 to-blue-500 text-white border-sky-300` };
  }
  if (demandCount > 20 && competition < 10000) {
    return { icon: '👍', text: 'Good', className: `${base} bg-gradient-to-r from-emerald-400 to-teal-500 text-white border-emerald-300` };
  }
  return { icon: '🎯', text: 'Niche', className: `${base} bg-gradient-to-r from-slate-400 to-slate-500 text-white border-slate-300` };
}

// --- UTILITY: Renders a list item or paragraph with bold formatting ---
const FormattedText = ({ text, as: Component = 'li', className = "text-sm" }) => {
  let processedText = (text || '').toString().trim();
  if (processedText.startsWith('*')) processedText = processedText.substring(1).trim();
  const sanitizedHtml = processedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  const finalClassName = `${className} text-slate-700 mb-1.5`;
  return <Component className={finalClassName} dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
};

// --- NEW COMPONENT: Rank History Graph Modal with time ranges ---
const RankHistoryModal = ({ isOpen, onClose, historyData, keyword }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [range, setRange] = useState('all'); // '7d' | '30d' | '90d' | '365d' | 'all'

  // Compute filtered history + best rank info
  const { labels, data, best } = useMemo(() => {
    const hist = Array.isArray(historyData) ? historyData : [];
    if (hist.length === 0) return { labels: [], data: [], best: null };

    let cutoff = 0;
    const now = Date.now();
    if (range === '7d') cutoff = now - 7 * 864e5;
    else if (range === '30d') cutoff = now - 30 * 864e5;
    else if (range === '90d') cutoff = now - 90 * 864e5;
    else if (range === '365d') cutoff = now - 365 * 864e5;

    const filtered = range === 'all' ? hist : hist.filter(h => h.date >= cutoff);

    // daily reduce
    const daily = new Map();
    filtered.forEach(h => {
      if (typeof h.date === 'number' && h.rank != null) {
        const key = new Date(h.date).toISOString().split('T')[0];
        daily.set(key, h.rank);
      }
    });

    const lbls = Array.from(daily.keys()).map(d =>
      new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: 'UTC', month: 'short', day: 'numeric' })
    );
    const points = Array.from(daily.values());

    // Best rank across ALL (not just filtered)
    let bestPoint = null;
    hist.forEach(h => {
      if (h.rank != null) {
        if (!bestPoint || h.rank < bestPoint.rank) bestPoint = h;
      }
    });
    return { labels: lbls, data: points, best: bestPoint };
  }, [historyData, range]);

  useEffect(() => {
    if (!isOpen || !chartRef.current) return;
    try {
      if (chartInstance.current) chartInstance.current.destroy();

      const ctx = chartRef.current.getContext('2d');
      chartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: 'Rank',
            data,
            borderColor: '#0ea5e9',
            backgroundColor: 'rgba(14, 165, 233, 0.1)',
            fill: true,
            tension: 0.3,
            pointRadius: 0,
            pointHoverRadius: 3,
            pointHitRadius: 8
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'nearest', intersect: false },
          elements: { point: { radius: 0, hoverRadius: 3, hitRadius: 8 } },
          plugins: {
            legend: { display: false },
            decimation: { enabled: true, algorithm: 'lttb', samples: 300 }
          },
          scales: {
            y: {
              reverse: true,
              beginAtZero: false,
              grid: { color: '#e2e8f0' },
              ticks: {
                color: '#64748b',
                stepSize: 1,
                callback: function (value) { if (Math.floor(value) === value) return value; }
              }
            },
            x: { grid: { display: false }, ticks: { color: '#64748b', maxRotation: 0, minRotation: 0 } }
          }
        }
      });
    } catch {
      // swallow render errors to avoid modal crash
    }

    return () => { if (chartInstance.current) chartInstance.current.destroy(); };
  }, [isOpen, labels, data]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-slate-800">Rank History for "{keyword}"</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-slate-100" aria-label="Close rank history">
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        {/* Range buttons */}
        <div className="flex flex-wrap gap-2 mb-3">
          {[
            { k: '7d', label: 'Last 7d' },
            { k: '30d', label: 'Month' },
            { k: '90d', label: 'Quarter' },
            { k: '365d', label: 'Year' },
            { k: 'all', label: 'All time' },
          ].map(btn => (
            <button
              key={btn.k}
              onClick={() => setRange(btn.k)}
              className={`text-xs px-2 py-1 rounded-md border ${range === btn.k ? 'bg-sky-600 border-sky-600 text-white' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'}`}
            >
              {btn.label}
            </button>
          ))}
        </div>

        <div className="h-72">
          <canvas ref={chartRef}></canvas>
        </div>

        {best && (
          <div className="mt-4 text-xs text-slate-600">
            <span className="font-semibold text-sky-700">Best rank: </span>
            <span className="font-bold text-sky-700">#{best.rank}</span>{' '}
            on {new Date(best.date).toLocaleDateString()}
          </div>
        )}
      </div>
    </div>
  );
};

// --- COMPONENT: Keyword Opportunities & Competitor Showcase ---
const KeywordOpportunitiesCard = ({ opportunities, competitors, onUseKeyword, onDownload }) => {
  const [competitionChecks, setCompetitionChecks] = useState({});
  const autoCheckedRef = useRef(new Set());

  const runCompetitionCheck = useCallback(async (keyword) => {
    setCompetitionChecks(prev => ({ ...prev, [keyword]: { status: 'loading' } }));
    try {
      const fn = httpsCallable(functionsInstance, 'fetchOpportunityCompetition');
      const result = await fn({ keyword });
      const { competition } = result.data || {};
      setCompetitionChecks(prev => ({ ...prev, [keyword]: { status: 'done', competition: competition ?? null } }));
    } catch (err) {
      console.error('fetchOpportunityCompetition (rank tracker) failed:', err);
      setCompetitionChecks(prev => ({ ...prev, [keyword]: { status: 'error' } }));
    }
  }, []);

  const handleCheckCompetition = useCallback((e, keyword) => {
    e.stopPropagation();
    runCompetitionCheck(keyword);
  }, [runCompetitionCheck]);

  // Auto-check the top 10 keywords with no cached data at all — anything already
  // cached is shown immediately and never auto-refetched.
  useEffect(() => {
    const topKeywords = (opportunities || [])
      .filter(o => o?.keyword && o.cachedCompetition == null && o.cachedPopularity == null)
      .slice(0, 10)
      .map(o => o.keyword);

    topKeywords.forEach(keyword => {
      if (autoCheckedRef.current.has(keyword)) return;
      autoCheckedRef.current.add(keyword);
      runCompetitionCheck(keyword);
    });
  }, [opportunities, runCompetitionCheck]);

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
      {/* Left Column: Keyword Suggestions */}
      <div className="flex flex-col">
        <div className="flex justify-between items-center mb-1 flex-shrink-0">
          <h2 className="font-semibold text-slate-800 text-lg">Keyword Opportunities</h2>
          {onDownload && (<button onClick={onDownload} className="flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold px-2 py-1 rounded-md transition-colors"><DownloadCloud size={14} />CSV</button>)}
        </div>
        <p className="text-sm text-slate-500 mb-1 flex-shrink-0">
          These are the suggested words that appear in the TPT search bar when typing your selected keyword. Keywords we already have data on show it right away — anything brand new gets checked automatically.
        </p>
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-block mb-3 flex-shrink-0 w-fit">
          📊 <strong>Popularity</strong> is currently untracked — TPT removed this data publicly. Previously searched popularity scores remain visible where available.
        </p>
        <div className="space-y-2 overflow-y-auto pr-2 flex-grow h-96 lg:h-auto lg:max-h-[28rem]">
          {!opportunities || opportunities.length === 0 ? (
            <p className="text-center text-sm text-slate-400 pt-8">No keyword suggestions were found for this search.</p>
          ) : (
            opportunities.map(item => {
              const check = competitionChecks[item.keyword] || { status: 'idle' };
              const hasCachedData = item?.cachedCompetition != null || item?.cachedPopularity != null;
              const cachedDateLabel = item?.cachedAt
                ? new Date(item.cachedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                : null;

              const comp = check.status === 'done' ? check.competition : (hasCachedData ? item.cachedCompetition : null);
              const demandCount = item?.cachedPopularity ?? null;
              const hasDemandData = demandCount != null && demandCount > 0;

              const badge = computeOpportunityBadge({ competition: comp, demandCount, hasDemandData });

              return (
                <div
                  key={item.keyword}
                  onClick={() => onUseKeyword?.(item.keyword)}
                  className="bg-slate-50/80 p-3 rounded-lg hover:bg-sky-50 border border-slate-200 hover:border-sky-300 cursor-pointer transition-all"
                >
                  <div className="flex justify-between items-start">
                    <p className="font-semibold text-slate-800 text-base">{item.keyword}</p>
                    {badge && (<span className={badge.className}>{badge.icon} {badge.text}</span>)}
                  </div>
                  <div className="flex items-center mt-2 text-sm flex-wrap gap-y-1">
                    <span className="font-medium text-slate-500 flex items-center gap-1">
                      {item.cachedPopularity != null ? (
                        <>
                          <span className="text-amber-500" title={cachedDateLabel ? `Cached from ${cachedDateLabel}` : 'From a previous search'}>★</span>
                          Popularity: <span className="font-bold text-sky-600">{item.cachedPopularity.toLocaleString()}</span>
                        </>
                      ) : (
                        <>Popularity: <span className="font-bold text-slate-400">—</span></>
                      )}
                    </span>
                    <span className="mx-3 text-slate-300">|</span>
                    {check.status === 'loading' ? (
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> Checking...
                      </span>
                    ) : check.status === 'error' ? (
                      <button onClick={(e) => handleCheckCompetition(e, item.keyword)} className="text-xs font-semibold text-red-500 hover:underline">
                        Failed — retry
                      </button>
                    ) : comp != null ? (
                      <span className="font-medium text-slate-500">
                        Competition: <span className="font-bold text-red-600">{comp.toLocaleString()}</span>
                        <button onClick={(e) => handleCheckCompetition(e, item.keyword)} className="ml-2 text-xs font-semibold text-sky-600 hover:text-sky-800 hover:underline">
                          Refresh
                        </button>
                      </span>
                    ) : (
                      <button onClick={(e) => handleCheckCompetition(e, item.keyword)} className="text-xs font-semibold text-sky-600 hover:text-sky-800 hover:underline">
                        Check competition
                      </button>
                    )}
                    {cachedDateLabel && check.status !== 'done' && (
                      <span className="w-full text-[11px] text-slate-400 italic mt-0.5">
                        Cached from {cachedDateLabel}
                      </span>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* Right Column: Top 5 Ranking Products */}
      <div className="flex flex-col">
        <h2 className="font-semibold text-slate-800 text-lg flex-shrink-0">Top 5 Ranking Products</h2>
        <p className="text-sm text-slate-500 mb-4 flex-shrink-0">A live look at the top-ranking products for this keyword right now.</p>
        <div className="space-y-3 overflow-y-auto pr-2 flex-grow h-96 lg:h-auto lg:max-h-[28rem]">
          {competitors?.filter(c => !c.isCurrentUser).slice(0, 5).map(comp => (
            <div key={comp.rank} className="flex items-start gap-3 p-2 rounded-lg bg-slate-50/70">
              <img src={comp.thumbnail} alt={comp.title} className="w-20 h-20 object-cover rounded-md border border-slate-200 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <a href={comp.url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-slate-700 hover:text-sky-600 line-clamp-2 leading-tight">{comp.title}</a>
                <div className="flex items-center text-xs mt-1 text-slate-500 flex-wrap">
                  <Star className="w-3.5 h-3.5 text-amber-400 fill-current mr-1" />
                  <span className="font-bold text-slate-600">{(comp.rating || 0).toFixed(2)}</span>
                  <span className="mx-1.5">({(comp.numRatings || 0).toLocaleString()} ratings)</span>
                </div>
                <div className="mt-1.5">
                  <span className="font-bold text-base text-green-700">${(comp.price || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// --- COMPONENT: Redesigned Competitive Analysis Card ---
const CompetitiveAnalysisCard = ({ analysis, titleSuggestions, currentTitleIndex, onCycleTitle, onDownload, onSaveTitle, isSavingTitle, titleSaveSuccess }) => {
  if (!analysis) return null;

  const displayTitle = titleSuggestions[currentTitleIndex] || "Generating title...";

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-semibold text-slate-800 text-lg">Competitive Analysis</h2>
        <button onClick={onDownload} className="flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold px-2 py-1 rounded-md transition-colors"><DownloadCloud size={14} />Download Insights</button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <h3 className="font-bold text-green-800 text-base flex items-center gap-2"><ThumbsUp size={18} /> Competitor Strengths</h3>
          <p className="text-xs text-green-700 mt-1 mb-3">Key selling points top competitors highlight.</p>
          <ul className="list-disc list-inside space-y-1">{analysis.commonThemes?.map((item, i) => <FormattedText key={i} text={item} />)}</ul>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h3 className="font-bold text-amber-800 text-base flex items-center gap-2"><AlertTriangle size={18} /> Your Improvement Areas</h3>
          <p className="text-xs text-amber-700 mt-1 mb-3">What your product may be missing.</p>
          <ul className="list-disc list-inside space-y-1">{analysis.gapAnalysis?.map((item, i) => <FormattedText key={i} text={item} />)}</ul>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-200">
        <h3 className="font-bold text-green-800 text-base flex items-center gap-2"><Sparkles size={16} /> Example Hooks from Competitors</h3>
        <ul className="list-disc list-inside space-y-1 pt-2 pl-2">
          {analysis.competitorHooks?.slice(0, 2).map((item, i) => <FormattedText key={i} text={`* "${item}"`} />)}
        </ul>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-200">
        <h3 className="font-semibold text-sky-700 text-base flex items-center gap-2"><Sparkles size={16} /> Recommended Title</h3>
        <div className="bg-slate-100 p-3 rounded-lg mt-2 flex justify-between items-center gap-2">
          <p className="text-sm text-slate-800">{displayTitle}</p>
          <div className="flex-shrink-0 flex items-center gap-2">
            <button onClick={onCycleTitle} className="text-xs flex items-center gap-1.5 bg-white hover:bg-slate-200 text-slate-600 font-semibold px-2 py-1.5 rounded-md border border-slate-300 transition-colors">
              <RefreshCw size={14} /> Generate Another
            </button>
            <button onClick={() => onSaveTitle(displayTitle)} disabled={isSavingTitle || titleSaveSuccess} className="flex items-center gap-1.5 text-xs bg-sky-500 hover:bg-sky-600 text-white font-semibold px-2 py-1.5 rounded-md transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed">
              {isSavingTitle ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {isSavingTitle ? 'Saving...' : (titleSaveSuccess ? 'Saved!' : 'Use This Title')}
            </button>
          </div>
        </div>
        <p className="text-[10px] text-slate-400 mt-1.5">
           Note: AI suggestions focus on high-volume keywords. Always review for your specific brand voice.
        </p>
      </div>
    </div>
  );
};

// --- COMPONENT: Reusable Component for simple keyword lists ---
const KeywordListCard = ({ title, description, data, onDownload }) => {
  const [copiedItem, setCopiedItem] = useState(null);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedItem(text);
      setTimeout(() => setCopiedItem(null), 1500);
    }).catch(err => console.error('Failed to copy:', err));
  };

  const items = Array.isArray(data) ? data : [];

  return (
    <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-200 flex flex-col h-full">
      <div className="flex justify-between items-center mb-1 flex-shrink-0">
        <h2 className="font-semibold text-slate-800 text-sm">{title}</h2>
        {onDownload && (<button onClick={onDownload} className="flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold px-2 py-1 rounded-md transition-colors"><DownloadCloud size={14} />CSV</button>)}
      </div>
      <p className="text-xs text-slate-500 mb-2 flex-shrink-0">{description}</p>
      <div className="space-y-1.5 overflow-y-auto pr-2 flex-grow min-h-[7rem] max-h-36">
        {items.length > 0 ? items.map(item => {
          const textToCopy = typeof item === 'string' ? item : item.word;
          const isCopied = copiedItem === textToCopy;
          return (
            <div key={`${textToCopy}-${item.frequency ?? ''}`} className="flex justify-between items-center group text-sm">
              <span className="text-slate-700">{textToCopy}</span>
              <div className="flex items-center">
                {item.frequency && <span className="font-mono font-bold text-sky-600 mr-2">{item.frequency}</span>}
                <button onClick={() => copyToClipboard(textToCopy)} className="opacity-0 group-hover:opacity-100 transition-opacity" aria-label={`Copy ${textToCopy}`}>
                  {isCopied ? <ClipboardCheck size={14} className="text-sky-500" /> : <Clipboard size={14} className="text-slate-400 hover:text-sky-500" />}
                </button>
              </div>
            </div>
          );
        }) : <p className="text-xs text-slate-400 text-center pt-4">No data available.</p>}
      </div>
    </div>
  );
};

// --- COMPONENT: Upgraded Keyword Summary Table with dual-delta filters & sorting ---
const KeywordSummaryTable = ({ summaries, isLoading, onUpdateRank, mostRecentCheck, updatingKeywords, onShowGraph, onDeleteKeyword, onRefreshAll, isUpdatingAll, formatRank, onRunAnalysisForKey }) => {
  const [sortBy, setSortBy] = useState({ key: 'lastChecked', dir: 'desc' });
  // filters: all | improved_recent | improved_alltime | dropped | top10
  const [filter, setFilter] = useState('all');

  const toggleSort = (key) => {
    setSortBy(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  };

  const computeRecentDelta = (s) =>
    (typeof s.previousRank === 'number' && typeof s.currentRank === 'number')
      ? (s.previousRank - s.currentRank) : null;

  const computeAllTimeDelta = (s) =>
    (typeof s.firstRank === 'number' && typeof s.currentRank === 'number')
      ? (s.firstRank - s.currentRank) : null;

  const filtered = useMemo(() => {
    const list = Array.isArray(summaries) ? summaries : [];
    switch (filter) {
      case 'top10':
        return list.filter(s => (s.currentRank ?? 9999) <= 10);
      case 'improved_recent':
        return list.filter(s => {
          const d = computeRecentDelta(s);
          return typeof d === 'number' && d > 0;
        });
      case 'improved_alltime':
        return list.filter(s => {
          const d = computeAllTimeDelta(s);
          return typeof d === 'number' && d > 0;
        });
      case 'dropped':
        return list.filter(s => {
          const d = computeRecentDelta(s);
          return typeof d === 'number' && d < 0;
        });
      default:
        return list;
    }
  }, [summaries, filter]);

  const valueForKey = (s, key) => {
    if (key === 'recentDelta') return computeRecentDelta(s) ?? 0;
    if (key === 'allTimeDelta') return computeAllTimeDelta(s) ?? 0;
    if (key === 'lastChecked' && s.lastChecked?.toDate) return s.lastChecked.toDate().getTime();
    return s[key] ?? 0;
  };

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const { key, dir } = sortBy;
    const mult = dir === 'asc' ? 1 : -1;
    return arr.sort((a, b) => {
      const A = valueForKey(a, key);
      const B = valueForKey(b, key);
      if (A === B) return 0;
      return (A > B ? 1 : -1) * mult;
    });
  }, [filtered, sortBy]);

  // Helper to render rank + page (e.g. "#107(5)")
  const renderRankWithPage = (rank, explicitPage) => {
    if (rank === null || typeof rank === 'undefined') return 'N/A';
    if (rank > 750) return '>750';
    // Fallback: Calculate page if explicitPage is missing (assuming ~24 items/page)
    const pageNum = explicitPage || Math.ceil(rank / 24);
    return (
      <span>
        #{rank}<span className="text-[10px] text-slate-400 font-normal ml-0.5">({pageNum})</span>
      </span>
    );
  };

  if (isLoading) return <div className="text-center p-4 text-sm text-slate-500">Loading keyword history...</div>;
  if (!Array.isArray(summaries) || summaries.length === 0) {
    return (
      <div className="text-center p-4 mt-4 border-t border-slate-200">
        <p className="text-sm font-semibold text-slate-700">No keywords tracked for this product yet.</p>
        <p className="text-xs text-slate-500 mt-1">Run an analysis to start tracking your keyword ranks.</p>
      </div>
    );
  }

  const sortIcon = (key) => sortBy.key === key ? (sortBy.dir === 'asc' ? '↑' : '↓') : '';

  return (
    <div className="mt-6 pt-4 border-t border-slate-200">
      <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
        <div>
          <h3 className="font-semibold text-slate-800 text-base">Tracked Keyword Summary</h3>
          <p className="text-xs text-slate-500">This table shows the lifetime ranking history for keywords you've audited for this product.</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Filter chips */}
          <div className="flex items-center gap-1 mr-2">
            {[
              { k: 'all', label: 'All' },
              { k: 'improved_recent', label: 'Improved (Recent)' },
              { k: 'improved_alltime', label: 'Improved (All-time)' },
              { k: 'dropped', label: 'Dropped' },
              { k: 'top10', label: 'Top 10' },
            ].map(f => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                className={`text-[11px] px-2 py-1 rounded-full border ${filter === f.k ? 'bg-sky-600 border-sky-600 text-white' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {mostRecentCheck && <p className="text-xs text-slate-400">Last data refreshed: {mostRecentCheck}</p>}
          <button
            onClick={onRefreshAll}
            disabled={isUpdatingAll || summaries.length === 0}
            className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-2 py-1 rounded-md flex items-center gap-1 disabled:opacity-50"
            title="Refresh all tracked keywords"
          >
            {isUpdatingAll ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh All
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
            <tr>
              <th className="p-3 font-medium cursor-pointer" onClick={() => toggleSort('keyword')}>Keyword {sortIcon('keyword')}</th>
              <th className="p-3 text-center font-medium cursor-pointer" onClick={() => toggleSort('firstRank')}>
                First Rank <span className="text-[10px] text-slate-400 font-normal normal-case">(Page)</span> {sortIcon('firstRank')}
              </th>
              <th className="p-3 text-center font-medium cursor-pointer" onClick={() => toggleSort('lowestRank')}>
                Highest Rank <span className="text-[10px] text-slate-400 font-normal normal-case">(Page)</span> {sortIcon('lowestRank')}
              </th>
              <th className="p-3 text-center font-medium cursor-pointer" onClick={() => toggleSort('highestRank')}>
                Lowest Rank <span className="text-[10px] text-slate-400 font-normal normal-case">(Page)</span> {sortIcon('highestRank')}
              </th>
              <th className="p-3 text-center font-medium cursor-pointer" onClick={() => toggleSort('currentRank')}>
                Current Rank <span className="text-[10px] text-slate-400 font-normal normal-case">(Page)</span> {sortIcon('currentRank')}
              </th>
              <th className="p-3 text-center font-medium cursor-pointer" onClick={() => toggleSort('allTimeDelta')}>All-time Δ {sortIcon('allTimeDelta')}</th>
              <th className="p-3 text-center font-medium cursor-pointer" onClick={() => toggleSort('recentDelta')}>Recent Δ {sortIcon('recentDelta')}</th>
              <th className="p-3 text-center font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(s => {
              const isUpdating = updatingKeywords.includes(s.keyword);
              const recentDelta = computeRecentDelta(s);
              const allTimeDelta = computeAllTimeDelta(s);

              const recentTooltip = (typeof s.previousRank === 'number' && typeof s.currentRank === 'number')
                ? `${recentDelta >= 0 ? 'Improved' : 'Dropped'} ${Math.abs(recentDelta)} since last check (${formatRank(s.previousRank)} → ${formatRank(s.currentRank)}).`
                : 'No recent comparison available.';

              const allTimeTooltip = (typeof s.firstRank === 'number' && typeof s.currentRank === 'number')
                ? `${allTimeDelta >= 0 ? 'Improved' : 'Dropped'} ${Math.abs(allTimeDelta)} since first check (${formatRank(s.firstRank)} → ${formatRank(s.currentRank)}).`
                : 'No all-time comparison available.';

              return (
                <tr key={s.id} className="border-b border-slate-100">
                  <td className="p-3 font-medium text-slate-700 align-middle break-words">{s.keyword}</td>

                  {/* First Rank + Page */}
                  <td className="p-3 text-center text-slate-600 align-middle">
                    {renderRankWithPage(s.firstRank, s.firstPage)}
                  </td>

                  {/* Highest Rank (Lowest Number) + Page */}
                  <td className="p-3 text-center text-green-600 font-semibold align-middle">
                    {renderRankWithPage(s.lowestRank, s.lowestPage)}
                  </td>

                  {/* Lowest Rank (Highest Number) + Page */}
                  <td className="p-3 text-center text-red-600 font-semibold align-middle">
                    {renderRankWithPage(s.highestRank, s.highestPage)}
                  </td>

                  {/* Current Rank + Page */}
                  <td className="p-3 text-center text-sky-600 font-bold align-middle">
                    {renderRankWithPage(s.currentRank, s.currentPage)}
                  </td>

                  {/* All-time Δ */}
                  <td className="p-3 text-center align-middle" title={allTimeTooltip}>
                    {typeof allTimeDelta === 'number' ? (
                      <span className={`inline-flex items-center font-semibold ${allTimeDelta > 0 ? 'text-green-600' : allTimeDelta < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                        {allTimeDelta > 0 && <ArrowUp size={14} className="mr-1" />}
                        {allTimeDelta < 0 && <ArrowDown size={14} className="mr-1" />}
                        {allTimeDelta === 0 ? '0' : `${allTimeDelta > 0 ? '+' : ''}${allTimeDelta}`}
                      </span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>

                  {/* Recent Δ */}
                  <td className="p-3 text-center align-middle" title={recentTooltip}>
                    {typeof recentDelta === 'number' ? (
                      <span className={`inline-flex items-center font-semibold ${recentDelta > 0 ? 'text-green-600' : recentDelta < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                        {recentDelta > 0 && <ArrowUp size={14} className="mr-1" />}
                        {recentDelta < 0 && <ArrowDown size={14} className="mr-1" />}
                        {recentDelta === 0 ? '0' : `${recentDelta > 0 ? '+' : ''}${recentDelta}`}
                      </span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>

                  <td className="p-3 text-center align-middle">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => onRunAnalysisForKey(s.keyword)}
                        disabled={isUpdating}
                        className="p-1.5 text-slate-500 hover:text-green-600 disabled:opacity-50"
                        title="Run Full Analysis"
                        aria-label={`Run full analysis for ${s.keyword}`}
                      >
                        <Play size={14} />
                      </button>
                      <button onClick={() => onUpdateRank(s.keyword)} disabled={isUpdating} className="p-1.5 text-slate-500 hover:text-sky-600 disabled:opacity-50" title="Update Rank" aria-label={`Update rank for ${s.keyword}`}>
                        {isUpdating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                      </button>
                      <button onClick={() => onShowGraph(s.id)} className="p-1.5 text-slate-500 hover:text-sky-600" title="View Rank History" aria-label={`View rank history for ${s.keyword}`}>
                        <BarChart3 size={14} />
                      </button>
                      <button onClick={() => onDeleteKeyword(s.id)} className="p-1.5 text-slate-500 hover:text-red-600" title="Delete Keyword History" aria-label={`Delete keyword ${s.keyword}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// --- NEW COMPONENT: Summary Tab ---
const SummaryTab = ({ data, onDownloadCsv, recommendation }) => {
  if (!data) return <div className="text-center p-8">Run a multi-keyword analysis to see a summary.</div>;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
        <h2 className="text-xl font-semibold text-slate-800 mb-1">Overall Strategic Summary</h2>
        <p className="text-sm text-slate-500 mb-4">This is a combined analysis of all keywords you searched. Use these master lists to guide your product title and description updates.</p>
        <div className="p-4 bg-sky-50/50 border border-sky-200 rounded-xl">
          <h3 className="font-semibold text-sky-700 text-base flex items-center gap-2"><Lightbulb size={16} /> High-Level Recommendations</h3>
          <FormattedText text={recommendation} as="p" className="text-sm" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <KeywordListCard title="Compiled Top Title Keywords and Phrases" description="The most frequent phrases from all top-ranking titles." data={data.titleAnalysis} onDownload={() => onDownloadCsv(data.titleAnalysis, 'title', 'summary_title_phrases')} />
        <KeywordListCard title="Compiled Description Keywords and Phrases" description="A master list of suggested keywords for your description." data={data.descriptionKeywords} onDownload={() => onDownloadCsv(data.descriptionKeywords, 'description', 'summary_description_keywords')} />
        <KeywordListCard title="Compiled Keyword Opportunities" description="All unique keyword opportunities found." data={data.keywordOpportunities.map(op => ({ word: op.keyword }))} onDownload={() => onDownloadCsv(data.keywordOpportunities, 'opportunities', 'summary_keyword_opportunities')} />
      </div>
    </div>
  )
};

// --- MAIN COMPONENT: SeoRankTracker ---
const SeoRankTracker = ({ currentUser, userTptProducts, onGoToRewrite, preselectedProductId, onClearPreselect, preselectedKeyword, onClearPreselectKeyword }) => {
  // State
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [keywordsInput, setKeywordsInput] = useState('');
  const [auditResults, setAuditResults] = useState({});
  const [activeTab, setActiveTab] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryRecommendation, setSummaryRecommendation] = useState("Run a multi-keyword analysis to generate a specific summary.");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [recentSearches, setRecentSearches] = useState([]);
  const [suggestedKeywords, setSuggestedKeywords] = useState([]);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [currentTitleIndex, setCurrentTitleIndex] = useState(0);
  const [keywordSummaries, setKeywordSummaries] = useState([]);
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);
  const [mostRecentCheck, setMostRecentCheck] = useState('');
  const [updatingKeywords, setUpdatingKeywords] = useState([]);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [titleSaveSuccess, setTitleSaveSuccess] = useState(false);
  const [isGraphModalOpen, setIsGraphModalOpen] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [graphData, setGraphData] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [keywordToDelete, setKeywordToDelete] = useState(null);
  const [isUpdatingAll, setIsUpdatingAll] = useState(false);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // --- NEW: Title Phrases State ---
  const [titlePhrases, setTitlePhrases] = useState([]);

  // --- NEW: State for interactive modal ---
  const [showInteractiveModal, setShowInteractiveModal] = useState(false);

  // Parse keywords (commas, new lines, semicolons)
  const parsedKeywords = useMemo(() =>
    keywordsInput.split(/[,\n;]+/).map(k => k.trim()).filter(Boolean), [keywordsInput]
  );

  // react-select styles
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: 'white',
      borderColor: state.isFocused ? '#0ea5e9' : '#cbd5e1',
      borderRadius: '0.5rem',
      padding: '0.35rem',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(14, 165, 233, 0.2)' : '0 1px 2px 0 rgb(0 0 0 / 0.05)',
      fontSize: '0.875rem',
      '&:hover': { borderColor: state.isFocused ? '#0ea5e9' : '#94a3b8' },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#0ea5e9' : state.isFocused ? '#f0f9ff' : 'white',
      color: state.isSelected ? 'white' : '#1e293b',
      fontSize: '0.875rem',
    }),
    singleValue: (provided, state) => ({ ...provided, display: state.selectProps.menuIsOpen ? 'none' : 'block' }),
    placeholder: (provided) => ({ ...provided, color: '#94a3b8' }),
  };

  const productOptions = useMemo(() => {
    if (!userTptProducts) return [];
    return userTptProducts
      .map(p => ({ value: p.id, label: p.productTitle }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [userTptProducts]);

  const handleProductChange = (selectedOption) => {
    const product = userTptProducts.find(p => p.id === selectedOption.value);
    setSelectedProduct(product);
  };

  // Effects
  useEffect(() => {
    if (preselectedProductId) return; // Don't auto-select if a preselection is active
    if (userTptProducts && userTptProducts.length > 0 && !selectedProduct) {
      setSelectedProduct(userTptProducts[0]);
    }
  }, [userTptProducts, selectedProduct, preselectedProductId]);

  useEffect(() => {
    if (selectedProduct && currentUser) {
      const savedSearches = localStorage.getItem(`tptSeoRecentSearches_${selectedProduct.id}`);
      setRecentSearches(savedSearches ? JSON.parse(savedSearches) : []);
      setAuditResults({});
      setActiveTab(null);
      setKeywordsInput('');
      setTitlePhrases([]); // Clear extracted phrases on product switch
      setIsSummaryLoading(true);
      const qy = query(collection(db, `users/${currentUser.uid}/products/${selectedProduct.id}/keywordSummaries`), orderBy("lastChecked", "desc"));
      const unsubscribe = onSnapshot(qy, (snapshot) => {
        const summaries = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setKeywordSummaries(summaries);
        if (summaries.length > 0 && summaries[0].lastChecked) setMostRecentCheck(new Date(summaries[0].lastChecked.toDate()).toLocaleString());
        else setMostRecentCheck('');
        setIsSummaryLoading(false);
      }, () => { setError("Could not load keyword history."); setIsSummaryLoading(false); });
      return () => unsubscribe();
    }
  }, [selectedProduct, currentUser]);

  useEffect(() => {
    if (Object.keys(auditResults).length > 1) {
      const allDescKeywords = new Set();
      const allTitlePhrases = [];
      const allOpportunities = new Map();
      for (const key in auditResults) {
        const result = auditResults[key] || {};
        result.actionCenter?.keywordsToAdd?.forEach(kw => allDescKeywords.add(kw));
        if (result.titleAnalysis) allTitlePhrases.push(...result.titleAnalysis);
        result.keywordOpportunities?.forEach(op => {
          if (!allOpportunities.has(op.keyword)) allOpportunities.set(op.keyword, op);
        });
      }
      const titleFreqMap = new Map();
      allTitlePhrases.forEach(phrase => titleFreqMap.set(phrase.word, (titleFreqMap.get(phrase.word) || 0) + phrase.frequency));
      const compiledTitleAnalysis = Array.from(titleFreqMap.entries())
        .map(([word, frequency]) => ({ word, frequency }))
        .sort((a, b) => b.frequency - a.frequency)
        .slice(0, 20);
      setSummaryData({
        descriptionKeywords: Array.from(allDescKeywords),
        titleAnalysis: compiledTitleAnalysis,
        keywordOpportunities: Array.from(allOpportunities.values())
      });
    } else {
      setSummaryData(null);
    }
  }, [auditResults]);

  useEffect(() => {
    const getSummaryRecs = async (data) => {
      if (!data) return;
      setSummaryRecommendation("Generating specific recommendations...");
      try {
        const getRecs = httpsCallable(functionsInstance, 'getSummaryRecommendations');
        const result = await getRecs({ summaryData: data });
        if (result.data.recommendation) setSummaryRecommendation(result.data.recommendation);
      } catch {
        setSummaryRecommendation("Could not generate a summary recommendation. Using default advice.");
      }
    };

    if (summaryData && activeTab === 'summary') getSummaryRecs(summaryData);
    else if (!summaryData) setSummaryRecommendation("Run a multi-keyword analysis to generate specific recommendations.");
  }, [summaryData, activeTab]);

  const currentAuditData = auditResults[activeTab];
  useEffect(() => {
    if (currentAuditData?.actionCenter?.titleSuggestion) {
      const suggestions = Array.isArray(currentAuditData.actionCenter.titleSuggestion)
        ? currentAuditData.actionCenter.titleSuggestion
        : [currentAuditData.actionCenter.titleSuggestion];
      setTitleSuggestions(suggestions.filter(Boolean));
      setCurrentTitleIndex(0);
    }
  }, [currentAuditData]);

  // Inline chart: fetch full history for active keyword
  const fetchFullHistoryForActiveTab = useCallback(async (keyword) => {
    if (!currentUser || !selectedProduct || !keyword) return [];
    const summary = keywordSummaries.find(s => s.keyword === keyword);
    if (!summary) return [];
    const historyQuery = query(
      collection(db, `users/${currentUser.uid}/products/${selectedProduct.id}/keywordSummaries/${summary.id}/history`),
      orderBy("date", "asc")
    );
    const snap = await getDocs(historyQuery);
    return snap.docs.map(d => ({ ...d.data(), date: d.data().date.toMillis() }));
  }, [currentUser, selectedProduct, keywordSummaries]);

  useEffect(() => {
    const draw = async () => {
      if (!activeTab || activeTab === 'summary' || !chartRef.current || !auditResults[activeTab]) {
        if (chartInstance.current) chartInstance.current.destroy();
        return;
      }

      let history = await fetchFullHistoryForActiveTab(activeTab);
      try {
        if (chartInstance.current) chartInstance.current.destroy();

        const dailyRanks = new Map();
        history.forEach(h => {
          if (h.date && typeof h.date === 'number') {
            const dateKey = new Date(h.date).toISOString().split('T')[0];
            dailyRanks.set(dateKey, h.rank);
          }
        });
        const labels = Array.from(dailyRanks.keys()).map(d => new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: 'UTC', month: 'short', day: 'numeric' }));
        const data = Array.from(dailyRanks.values());

        const ctx = chartRef.current.getContext('2d');
        chartInstance.current = new Chart(ctx, {
          type: 'line',
          data: { labels, datasets: [{ label: 'Rank', data, borderColor: '#0ea5e9', backgroundColor: 'rgba(14,165,233,0.1)', fill: true, tension: 0.3, pointRadius: 0, pointHoverRadius: 3, pointHitRadius: 8 }] },
          options: {
            responsive: true, maintainAspectRatio: false,
            interaction: { mode: 'nearest', intersect: false },
            elements: { point: { radius: 0, hoverRadius: 3, hitRadius: 8 } },
            plugins: { legend: { display: false }, decimation: { enabled: true, algorithm: 'lttb', samples: 300 } },
            scales: {
              y: { reverse: true, beginAtZero: false, grid: { color: '#e2e8f0' }, ticks: { color: '#64748b', stepSize: 1, callback: v => (Math.floor(v) === v ? v : '') } },
              x: { grid: { display: false }, ticks: { color: '#64748b', maxRotation: 0, minRotation: 0 } }
            }
          }
        });
      } catch {
        // ignore draw errors
      }
    };
    draw();
    return () => { if (chartInstance.current) chartInstance.current.destroy(); };
  }, [activeTab, auditResults, fetchFullHistoryForActiveTab]);

  // 🔗 Deep-link from Hub: focus & scroll to that product once.
  useEffect(() => {
    if (!preselectedProductId || !userTptProducts || userTptProducts.length === 0) return;

    const productToSelect = userTptProducts.find(p => p.id === preselectedProductId);
    if (productToSelect) {
        setSelectedProduct(productToSelect);

        // Scroll the main section into view for focus
        setTimeout(() => {
            document
              .getElementById("seo-setup-section")
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    }

    onClearPreselect?.();
  }, [preselectedProductId, onClearPreselect, userTptProducts]);


  // Handlers
  const handleCycleTitle = () => {
    if (titleSuggestions.length > 0) {
      setCurrentTitleIndex(prevIndex => (prevIndex + 1) % titleSuggestions.length);
    }
  };

  const downloadCsvViaBlob = (csvRows, filename) => {
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleDownloadCsv = (data, type, keyword) => {
    if (!data) return;
    if (analytics) logEvent(analytics, 'download_keyword_csv', { keyword, type });

    const safeKeyword = (keyword || 'export').replace(/[^\w.-]+/g, '_');
    const rows = [];

    if (type === 'title') {
      rows.push('Phrase,Frequency');
      data.forEach(item => rows.push(`"${(item.word || '').replace(/"/g, '""')}",${item.frequency ?? 0}`));
    } else if (type === 'description') {
      rows.push('Keywords to Add');
      data.forEach(item => rows.push(`"${(item || '').toString().replace(/"/g, '""')}"`));
    } else if (type === 'opportunities') {
      rows.push('Keyword,Popularity,Competition');
      data.forEach(item => rows.push(`"${(item.keyword || '').replace(/"/g, '""')}",${item.cachedPopularity ?? ''},${item.cachedCompetition ?? ''}`));
    }

    downloadCsvViaBlob(rows, `${safeKeyword}.csv`);
  };

  const handleDownloadFeedback = (data, keyword) => {
    if (!data) return;
    if (analytics) logEvent(analytics, 'download_seo_feedback', { keyword });
    const { descriptionAnalysis = {}, actionCenter = {} } = data;
    const formatForTxt = (text) => Array.isArray(text) ? text.join('\n') : (text || '').replace(/\*/g, '').replace(/<[^>]*>/g, '');
    let textContent = `SEO AUDIT FEEDBACK FOR KEYWORD: "${keyword}"\n`;
    textContent += `=================================================\n\n`;
    textContent += `== COMPETITOR STRENGTHS ==\n${formatForTxt(descriptionAnalysis.commonThemes)}\n\n`;
    textContent += `== YOUR IMPROVEMENT AREAS ==\n${formatForTxt(descriptionAnalysis.gapAnalysis)}\n\n`;
    textContent += `== RECOMMENDED TITLE REWRITE ==\n${(actionCenter.titleSuggestion || []).join('\n')}\n\n`;
    const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `SEO_Feedback_${(keyword || 'keyword').replace(/[^\w.-]+/g, '_')}.txt`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleGenerateKeywords = async () => {
    if (!selectedProduct) return;
    setIsSuggesting(true); setSuggestedKeywords([]);
    if (analytics) logEvent(analytics, 'generate_related_keywords', { product_id: selectedProduct.id });
    try {
      const generateKeywordsFunc = httpsCallable(functionsInstance, 'generateRelatedKeywords');
      const result = await generateKeywordsFunc({ productId: selectedProduct.id });
      setSuggestedKeywords(result.data.keywords || []);
    } catch {
      setError("Could not generate keywords at this time.");
    } finally { setIsSuggesting(false); }
  };

  // --- NEW: Extract Keywords from Title Feature (Updated Logic) ---
  const handleExtractFromTitle = () => {
    if (!selectedProduct || !selectedProduct.productTitle) {
      setError("Select a product with a title first.");
      return;
    }

    const title = selectedProduct.productTitle;
    // 1. Clean title: remove special chars (except space), squash whitespace
    const clean = title.replace(/[^a-zA-Z0-9 ]/g, " ").replace(/\s+/g, " ").trim();
    if (!clean) return;

    const words = clean.split(" ");
    const phrases = new Set();
    const STOP_WORDS = new Set(["and", "the", "for", "with", "of", "in", "a", "to", "is", "on", "at", "by"]);

    // Helper to add if not stopword/short
    const addPhrase = (p) => {
        const cleanP = p.trim();
        if (cleanP.length > 2 && !STOP_WORDS.has(cleanP.toLowerCase())) {
            phrases.add(cleanP);
        }
    };

    // A. Full title if it's not insanely long (e.g. keyword stuffing check)
    if (words.length <= 6) addPhrase(clean);

    // B. Trigrams (3 words)
    if (words.length >= 3) {
        for (let i = 0; i < words.length - 2; i++) {
            addPhrase(`${words[i]} ${words[i+1]} ${words[i+2]}`);
        }
    }

    // C. Bigrams (2 words)
    if (words.length >= 2) {
        for (let i = 0; i < words.length - 1; i++) {
             addPhrase(`${words[i]} ${words[i+1]}`);
        }
    }

    // D. Single words if we are desperate (less than 5 phrases found so far)
    if (phrases.size < 5) {
        words.forEach(w => addPhrase(w));
    }

    // Limit to MAX (e.g. 10) to prevent breaking analysis
    const MAX_PHRASES = 10;
    const newPhrases = Array.from(phrases).slice(0, MAX_PHRASES);

    setTitlePhrases(newPhrases);
  };

  const handleRunAudit = async (keywordsToRun, options = {}) => {
    const { clearPrevious = false } = options;
    const keywords = Array.isArray(keywordsToRun)
      ? keywordsToRun
      : keywordsInput.split(/[,\n;]+/).map(k => k.trim().toLowerCase()).filter(Boolean);

    if (!selectedProduct || keywords.length === 0) {
      setError("Select a product, then enter search phrases separated by commas.");
      return;
    }

    setIsLoading(true);
    setError('');

    if (clearPrevious) {
      setAuditResults({});
      setActiveTab(null);
      setSummaryData(null);
    }

    if (analytics) logEvent(analytics, 'run_seo_audit', { product_id: selectedProduct.id, keywords: keywords.join(',') });
    const newRecentSearches = [...new Set([...keywords, ...recentSearches])].slice(0, 10);
    setRecentSearches(newRecentSearches); localStorage.setItem(`tptSeoRecentSearches_${selectedProduct.id}`, JSON.stringify(newRecentSearches));
    try {
      const runSeoAudit = httpsCallable(functionsInstance, 'runSeoAudit');
      const results = {};
      const settledResults = await Promise.allSettled(keywords.map(keyword => runSeoAudit({ productId: selectedProduct.id, keyword })));
      settledResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          results[keywords[index]] = result.value.data;
        } else {
          console.error(result.reason);
          throw new Error(`Analysis failed for "${keywords[index]}".`);
        }
      });

      setAuditResults(prevResults => ({
        ...prevResults,
        ...results
      }));
      setActiveTab(keywords[0]);
      setShowResultsModal(true);
    } catch (err) { setError(err.message || "An expert-level analysis could not be completed."); }
    finally { setIsLoading(false); }
  };

  const handleRunSingleKeywordAnalysis = async (keyword) => {
    if (!selectedProduct) {
      setError("Please select a product first.");
      return;
    }
    await handleRunAudit([keyword], { clearPrevious: false });
  };

  const handleUpdateRank = async (keyword) => {
    if (!selectedProduct) return;
    setUpdatingKeywords(prev => [...prev, keyword]);
    try {
      const updateRankFunc = httpsCallable(functionsInstance, 'updateKeywordRank');
      await updateRankFunc({ productId: selectedProduct.id, keyword: keyword });
    } catch {
      setError(`Could not update rank for "${keyword}".`);
    } finally {
      setUpdatingKeywords(prev => prev.filter(k => k !== keyword));
    }
  };

  const handleUpdateAllRanks = async () => {
    if (!selectedProduct || keywordSummaries.length === 0) return;
    setIsUpdatingAll(true);
    try {
      const updateRankFunc = httpsCallable(functionsInstance, 'updateKeywordRank');
      const BATCH = 5;
      const keywords = keywordSummaries.map(s => s.keyword);
      for (let i = 0; i < keywords.length; i += BATCH) {
        const batch = keywords.slice(i, i + BATCH);
        await Promise.allSettled(batch.map(k => updateRankFunc({ productId: selectedProduct.id, keyword: k })));
      }
    } catch {
      setError("Bulk refresh failed for some keywords.");
    } finally {
      setIsUpdatingAll(false);
    }
  };

  const handleSaveTitle = async (newTitle) => {
    if (!selectedProduct || !newTitle) return;
    setIsSavingTitle(true); setTitleSaveSuccess(false);
    try {
      const saveProduct = httpsCallable(functionsInstance, 'saveUserProductDescription');
      await saveProduct({ productId: selectedProduct.id, productData: { productTitle: newTitle } });
      setTitleSaveSuccess(true); setTimeout(() => setTitleSaveSuccess(false), 2000);
    } catch {
      setError("Could not save the new title.");
    } finally { setIsSavingTitle(false); }
  };

  const handleRecentKeywordClick = (keyword) => {
    const existing = new Set(keywordsInput.split(/[,\n;]+/).map(k => k.trim()).filter(Boolean));
    if (!existing.has(keyword)) {
      const next = existing.size > 0 ? `${keywordsInput.trim().replace(/[\s,]+$/,'')}, ${keyword}` : keyword;
      setKeywordsInput(next);
    }
  };

  const handleDeleteKeyword = async () => {
    if (!keywordToDelete || !currentUser || !selectedProduct) return;
    try {
      const keywordDocRef = doc(db, `users/${currentUser.uid}/products/${selectedProduct.id}/keywordSummaries`, keywordToDelete);
      await deleteDoc(keywordDocRef);
      if (analytics) logEvent(analytics, 'delete_keyword_summary', { product_id: selectedProduct.id, keyword: keywordToDelete });
    } catch (err) {
      console.error("Error deleting keyword summary:", err);
      setError("Failed to delete keyword history.");
    } finally {
      setKeywordToDelete(null);
      setShowDeleteConfirm(false);
    }
  };

  const openDeleteConfirm = (keywordId) => {
    setKeywordToDelete(keywordId);
    setShowDeleteConfirm(true);
  };

  const openGraphModal = useCallback(async (keywordId) => {
    const summary = keywordSummaries.find(s => s.id === keywordId);
    if (!summary || !currentUser || !selectedProduct) return;

    try {
        const historyQuery = query(collection(db, `users/${currentUser.uid}/products/${selectedProduct.id}/keywordSummaries/${keywordId}/history`), orderBy("date", "asc"));
        const historySnapshot = await getDocs(historyQuery);
        const history = historySnapshot.docs.map(doc => {
            const data = doc.data();
            return { ...data, date: data.date.toMillis() };
        });
        setGraphData({ history, keyword: summary.keyword });
        setIsGraphModalOpen(true);
    } catch(e) {
        console.error("Failed to fetch graph history", e);
        setError("Could not load rank history.");
    }
}, [currentUser, selectedProduct, keywordSummaries]);


  const latestRankInfo = auditResults[activeTab]?.rankHistory?.[auditResults[activeTab].rankHistory.length - 1];

  const handleUseKeywordFromOpportunity = (kw) => setKeywordsInput(kw);

  const formatRank = (rank) => {
    if (rank === null || typeof rank === 'undefined') {
      return 'N/A';
    }
    if (rank > 750) {
      return '>750';
    }
    return `#${rank}`;
  };

  useEffect(() => {
    if (preselectedKeyword && selectedProduct && keywordSummaries.length > 0) {
        const targetSummary = keywordSummaries.find(s => s.keyword === preselectedKeyword);
        if (targetSummary) {
            openGraphModal(targetSummary.id);
            onClearPreselectKeyword?.();
        }
    }
  }, [preselectedKeyword, selectedProduct, keywordSummaries, openGraphModal, onClearPreselectKeyword]);

  return (
    <div className="space-y-8 font-sans">
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        message={`Are you sure you want to delete all tracking data for the keyword "${keywordToDelete}"? This action cannot be undone.`}
        onConfirm={handleDeleteKeyword}
        onCancel={() => setShowDeleteConfirm(false)}
      />
      <RankHistoryModal
        isOpen={isGraphModalOpen}
        onClose={() => setIsGraphModalOpen(false)}
        historyData={graphData?.history}
        keyword={graphData?.keyword}
      />

      <section id="seo-setup-section" className="relative bg-white p-6 sm:p-8 rounded-2xl shadow-lg border border-slate-200">

        {/* --- MODIFIED HEADER ACTIONS --- */}
        <div className="absolute top-6 right-6 flex items-center gap-2">
            <button
                type="button"
                onClick={() => {
                    setShowInteractiveModal(true);
                    if (analytics) {
                        logEvent(analytics, 'guide_click', {
                            content_type: 'interactive_walkthrough',
                            item_id: 'seo_rank_tracker_interactive'
                        });
                    }
                }}
                className="flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold px-2 py-1 rounded-md transition-colors"
            >
                <MousePointerClick size={14} />
                Interactive Walkthrough
            </button>

            <a
              href="https://youtu.be/ZeYOWrIC8AI?si=3yPM3HVwNqBpmx0G"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold px-2 py-1 rounded-md transition-colors"
            >
              <Video size={14} />
              Watch Tutorial
            </a>
        </div>
        {/* --- END HEADER ACTIONS --- */}

        <h2 className="text-xl font-semibold text-sky-700 flex items-center"><Sparkles className="h-6 w-6 mr-2 text-sky-600" />Keyword & Rank Analysis</h2>
        <p className="text-xs text-slate-500 mt-2 mb-6">Select a product, then enter search phrases <strong>separated by commas</strong> (we recommend 3 at a time) to check your TPT rank and see competitor insights. Load time depends on how deep your product ranks — the further down, the longer it takes.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
          <div>
            <label htmlFor="product-select" className="block text-sm font-medium text-slate-700 mb-1">1. Select Product</label>
            <Select
              id="product-select"
              options={productOptions}
              value={selectedProduct ? { value: selectedProduct.id, label: selectedProduct.productTitle } : null}
              onChange={handleProductChange}
              styles={customStyles}
              placeholder={userTptProducts?.length === 0 ? "Add a product first..." : "Select or type to search..."}
              isDisabled={!userTptProducts || userTptProducts.length === 0}
            />
          </div>

          <div>
            <label htmlFor="seo-keyword-input" className="block text-sm font-medium text-slate-700 mb-1">
              2. Enter Keywords <span className="font-normal text-slate-500">(separate with commas)</span>
            </label>
            <div className="relative">
              <input
                id="seo-keyword-input"
                value={keywordsInput}
                onChange={(e) => setKeywordsInput(e.target.value)}
                placeholder="e.g., grade 4 math, fraction worksheets grade 4, maths"
                className="w-full p-3 pr-10 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500 text-sm"
              />
              <button
                onClick={handleExtractFromTitle}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-sky-600 bg-white hover:bg-slate-100 rounded-md transition-colors"
                title="Auto-generate search phrases from your current product title"
              >
                <Flashlight size={16} />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-transparent mb-1 select-none">Run</label>
            <button
              onClick={() => handleRunAudit(parsedKeywords, { clearPrevious: true })}
              disabled={isLoading || !selectedProduct || parsedKeywords.length === 0}
              className="w-full h-[46px] flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-bold rounded-lg shadow-md transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed"
            >
              {isLoading ? <Loader2 className="animate-spin" /> : <RefreshCw size={18} />}
              <span>{isLoading ? 'Analyzing...' : 'Run Full Analysis'}</span>
            </button>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
          <p className="text-[12px] text-slate-500">
            Tip: Use the <Flashlight size={12} className="inline mb-0.5" /> icon to instantly break your title into checkable phrases.
          </p>
          {parsedKeywords.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              <span className="text-[11px] text-slate-500 mr-1">We’ll analyze:</span>
              {parsedKeywords.map((kw, i) => (
                <span key={`${kw}-${i}`} className="text-[11px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full border border-slate-200">{kw}</span>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">

            {/* --- NEW: Phrases from Title Column --- */}
            {titlePhrases.length > 0 && (
              <div>
                 <div className="flex items-center gap-2">
                  <Flashlight size={16} className="text-sky-600" />
                  <h3 className="text-sm font-medium text-sky-600">From Your Title</h3>
                </div>
                <p className="text-xs text-slate-500 mt-1 mb-2">Click to add title phrases.</p>
                <div className="flex flex-wrap gap-2">
                  {titlePhrases.map((phrase, i) => (
                    <button
                      key={i}
                      onClick={() => handleRecentKeywordClick(phrase)}
                      className="text-xs bg-purple-50 text-purple-700 hover:bg-purple-600 hover:text-white border border-purple-200 px-2 py-1 rounded-full transition-colors"
                    >
                      + {phrase}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-sky-600" />
                <h3 className="text-sm font-medium text-sky-600">Recently Searched</h3>
              </div>
              <p className="text-xs text-slate-500 mt-1 mb-2">Click any item to add it back into the search box.</p>
              <div className="flex flex-wrap gap-2">
                {recentSearches.length > 0 ? recentSearches.map((search, i) => (
                  <button
                    key={i}
                    onClick={() => handleRecentKeywordClick(search)}
                    className="text-xs bg-slate-100 text-slate-600 hover:bg-sky-600 hover:text-white px-2 py-1 rounded-full transition-colors"
                  >
                    + {search}
                  </button>
                )) : <p className="text-xs text-slate-400">No recent searches.</p>}
              </div>
            </div>

            <div>
              <button
                onClick={handleGenerateKeywords}
                disabled={isSuggesting || !selectedProduct}
                className="text-sm font-medium text-sky-600 hover:text-sky-800 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSuggesting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                <span>Generate Related Keyword Ideas</span>
              </button>
              <p className="text-xs text-slate-500 mt-1">Click to get suggested search terms based on your product.</p>
              {suggestedKeywords.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {suggestedKeywords.map((kw, i) => (
                    <button
                      key={i}
                      onClick={() => handleRecentKeywordClick(kw)}
                      className="text-xs bg-sky-100 text-sky-700 hover:bg-sky-200 px-2 py-1 rounded-full transition-colors"
                    >
                      + {kw}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <p className="text-xs text-slate-500 text-center mt-6 -mb-2">
          Note: A rank of <strong className="font-semibold text-slate-600">>750</strong> indicates the product was not found within the top 750 search results.
        </p>

        <KeywordSummaryTable
          summaries={keywordSummaries}
          isLoading={isSummaryLoading}
          onUpdateRank={handleUpdateRank}
          mostRecentCheck={mostRecentCheck}
          updatingKeywords={updatingKeywords}
          onShowGraph={openGraphModal}
          onDeleteKeyword={openDeleteConfirm}
          onRefreshAll={handleUpdateAllRanks}
          isUpdatingAll={isUpdatingAll}
          formatRank={formatRank}
          onRunAnalysisForKey={handleRunSingleKeywordAnalysis}
        />

        {error && <p className="text-red-600 bg-red-50 p-3 rounded-lg text-sm mt-4">{error}</p>}

        {!isLoading && Object.keys(auditResults).length > 0 && !showResultsModal && (
          <button
            onClick={() => setShowResultsModal(true)}
            className="mt-4 flex items-center gap-2 text-sm font-semibold text-sky-600 hover:text-sky-800 bg-sky-50 hover:bg-sky-100 px-4 py-2 rounded-lg transition-colors"
          >
            <BarChart3 size={16} /> View Analysis Results
          </button>
        )}
      </section>

      {isLoading && !activeTab && (
        <div className="text-center py-12">
          <Loader2 className="h-12 w-12 text-sky-600 animate-spin mx-auto" />
          <p className="mt-4 text-slate-500 font-semibold">Performing expert analysis... this may take a moment.</p>
        </div>
      )}

      {showResultsModal && Object.keys(auditResults).length > 0 && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/60 p-4 overflow-y-auto"
          onClick={() => setShowResultsModal(false)}
        >
          <div
            className="w-full max-w-7xl bg-slate-50 rounded-2xl shadow-2xl my-4 sm:my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center px-6 py-4 bg-white border-b border-slate-200 rounded-t-2xl sticky top-0 z-10">
              <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Layers className="h-5 w-5 text-sky-600" /> Keyword &amp; Rank Analysis
              </h2>
              <button
                type="button"
                onClick={() => setShowResultsModal(false)}
                className="text-slate-400 hover:text-slate-700 p-1.5 rounded-full hover:bg-slate-100"
                aria-label="Close results"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
        <div className="animate-fade-in">
          <div className="border-b border-slate-200 mb-6">
            <nav className="-mb-px flex space-x-2 overflow-x-auto" aria-label="Tabs">
              {Object.keys(auditResults).map(keyword => (
                <button
                  key={keyword}
                  onClick={() => setActiveTab(keyword)}
                  className={`whitespace-nowrap py-3 px-4 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${activeTab === keyword ? 'bg-white text-sky-600 border-sky-500 shadow-sm' : 'bg-transparent text-slate-500 border-transparent hover:bg-slate-100'}`}
                >
                  "{keyword}"
                </button>
              ))}
              {summaryData && (
                <button
                  onClick={() => setActiveTab('summary')}
                  className={`whitespace-nowrap py-3 px-4 text-sm font-semibold rounded-t-lg border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'summary' ? 'bg-white text-sky-600 border-sky-500 shadow-sm' : 'bg-transparent text-slate-500 border-transparent hover:bg-slate-100'}`}
                >
                  <Layers size={14} /> Summary
                </button>
              )}
            </nav>
          </div>

          {activeTab === 'summary' ? (
            <SummaryTab data={summaryData} onDownloadCsv={handleDownloadCsv} recommendation={summaryRecommendation} />
          ) : currentAuditData && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-200 flex flex-col justify-center text-center h-full">
                  <h2 className="font-semibold text-slate-800 text-sm">Current Rank</h2>
                  <p className="text-5xl font-extrabold text-sky-600 my-1">{formatRank(latestRankInfo?.rank)}</p>
                  <p className="text-xs text-slate-500">{latestRankInfo ? `On Page ${latestRankInfo.page}` : 'Not found in top results'}</p>
                </div>
                <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-200 flex flex-col h-full">
                  <h2 className="font-semibold text-slate-800 text-sm mb-1">Rank History</h2>
                  <p className="text-xs text-slate-400 mb-2">All-time performance for this keyword</p>
                  <div className="flex-grow min-h-[7rem]"><canvas ref={chartRef}></canvas></div>
                </div>
                <KeywordListCard
                  title="Top Title Keywords and Phrases"
                  description="Common phrases from top 30 titles."
                  data={currentAuditData.titleAnalysis}
                  onDownload={() => handleDownloadCsv(currentAuditData.titleAnalysis, 'title', activeTab)}
                />
                <KeywordListCard
                  title="Description Keywords and Phrases"
                  description="Suggested terms to add to your description."
                  data={(currentAuditData.actionCenter?.keywordsToAdd || []).map(kw => ({ word: kw }))}
                  onDownload={() => handleDownloadCsv(currentAuditData.actionCenter?.keywordsToAdd || [], 'description', activeTab)}
                />
              </div>

              <KeywordOpportunitiesCard
                opportunities={currentAuditData.keywordOpportunities}
                competitors={currentAuditData.serpVisualization}
                onUseKeyword={handleUseKeywordFromOpportunity}
                onDownload={() => handleDownloadCsv(currentAuditData.keywordOpportunities, 'opportunities', activeTab + '_opportunities')}
              />

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <div className="lg:col-span-3">
                  <CompetitiveAnalysisCard
                    analysis={currentAuditData.descriptionAnalysis}
                    titleSuggestions={titleSuggestions}
                    currentTitleIndex={currentTitleIndex}
                    onCycleTitle={handleCycleTitle}
                    onDownload={() => handleDownloadFeedback(currentAuditData, activeTab)}
                    onSaveTitle={handleSaveTitle}
                    isSavingTitle={isSavingTitle}
                    titleSaveSuccess={titleSaveSuccess}
                  />
                </div>
                <div className="lg:col-span-2">
                  <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 h-full">
                    <h2 className="font-semibold text-slate-800 text-lg">Strategy & Action Center</h2>
                    <p className="text-sm text-slate-500 mb-4">Use these recommendations to improve your product listing and marketing.</p>
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold text-sky-700 text-base flex items-center gap-2"><Sparkles size={16} /> Hook & Opening Line Ideas</h3>
                        <ul className="mt-2 list-disc list-inside space-y-1.5 pl-2">
                          {currentAuditData.actionCenter?.openingLine?.map((s, i) => <FormattedText key={i} text={s} />)}
                          {!currentAuditData.actionCenter?.openingLine && currentAuditData.competitorDescriptions?.slice(0, 2).map((desc, i) => (
                            <li key={i} className="text-xs text-slate-600 italic">Example: "{(desc || '').substring(0, 100).replace(/<[^>]*>?/gm, '')}..."</li>
                          ))}
                          {!currentAuditData.actionCenter?.openingLine && (!currentAuditData.competitorDescriptions || currentAuditData.competitorDescriptions.length === 0) && <li className="text-sm text-slate-400">Run analysis to generate ideas.</li>}
                        </ul>
                      </div>
                      <div className="pt-4 border-t border-slate-200">
                        <h3 className="font-semibold text-sky-700 text-base flex items-center gap-2"><Lightbulb size={16} /> Creative Content Angles</h3>
                        <ul className="mt-2 list-disc list-inside space-y-1.5 pl-2">{currentAuditData.actionCenter?.contentAngle?.map((s, i) => <FormattedText key={i} text={s} />)}</ul>
                      </div>
                      <div className="pt-4 border-t border-slate-200">
                        <h3 className="font-semibold text-sky-700 text-base flex items-center gap-2"><ArrowUp size={16} /> Value Proposition Boosters</h3>
                        <ul className="mt-2 list-disc list-inside space-y-1.5 pl-2">{currentAuditData.actionCenter?.valueProposition?.map((s, i) => <FormattedText key={i} text={s} />)}</ul>
                      </div>
                      <div className="pt-4 border-t border-slate-200">
                        <button onClick={() => onGoToRewrite(selectedProduct)} className="w-full flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-bold px-3 py-3 text-sm rounded-lg transition-colors">
                          <Edit size={16} /><span>Go to Description Rewriter</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* --- NEW INTERACTIVE WALKTHROUGH MODAL --- */}
      {showInteractiveModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowInteractiveModal(false)}
        >
          {/* Use max-w-7xl for a wider modal */}
          <div
            className="w-full max-w-7xl rounded-xl bg-slate-800 border border-slate-700 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b border-slate-700 flex-shrink-0">
              <h3 className="text-lg font-semibold text-sky-400">
                Interactive Walkthrough
              </h3>
              <button
                type="button"
                onClick={() => setShowInteractiveModal(false)}
                className="text-slate-300 hover:text-white p-1 rounded-full hover:bg-slate-700"
                aria-label="Close modal"
              >
                <X size={24} />
              </button>
            </div>

            {/* New Embed Code */}
            <div
                style={{
                    position: 'relative',
                    boxSizing: 'content-box',
                    maxHeight: '80vh',
                    // Note: max-height can't be repeated in JS style obj; using one
                    width: '100%',
                    aspectRatio: '2.013986013986014',
                    padding: '40px 0 40px 0',
                    background: '#1e293b' // Keep dark background for loading state
                }}
            >
                <iframe
                    src="https://app.supademo.com/embed/cmi4gl95i1dc4b7b4ld7fttdh?embed_v=2&utm_source=embed"
                    loading="lazy"
                    title="Mr Joel's Seller Tool - TPT SEO Rank Tracker"
                    allow="clipboard-write"
                    frameBorder="0"
                    webkitallowfullscreen="true"
                    mozallowfullscreen="true"
                    allowFullScreen
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%'
                    }}
                ></iframe>
            </div>
          </div>
        </div>
      )}
      {/* --- END INTERACTIVE WALKTHROUGH MODAL --- */}
    </div>
  );
};

export default SeoRankTracker;